import React, { useMemo, useEffect } from 'react';
import get from 'lodash/get';
import isNumber from 'lodash/isNumber';
import { v4 as uuidv4 } from 'uuid';

import {
  removeUnexistingProducts,
  removeProductsForCorrectSize,
  getProductsLength,
} from '../bundleUtils';
import {
  getIdFromGid,
  getGidFromIdForResource,
  getAddonPrice,
  getProductCurrentPrice,
  getPriceFromSellingPlanAllocations,
  isGlobalProductList,
  isGlobalProduct,
  isProductAvailable,
} from '../../utils';
import { applyDiscountsToLine } from '../../subscriptions/utils';
import { useGlobalContext } from '../../subscriptions/state/GlobalContext';

import { getDynamicSubscriptionLinePrices } from '../../utils/BundleUtils';
import { removeOutOfStockVariants } from '../../subscriptions/BundleUpdate/utils';
import merge from 'lodash/merge';
import flatMap from 'lodash/flatMap';
import omit from 'lodash/omit';
import {
  getDiscountFromSellingPlan,
  getDiscountFromUrl,
  getItemPropertiesFromState,
} from './utils';
import { useBundleSetting } from '../../subscriptions/state/BundleSettingContext';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { getBundleDiscounts } from '../api';

const ONE_TIME_SELLING_PLAN = 'onetime';
export interface UseCreateBundle {
  isFullSize: boolean;
  isSaveable: boolean;
  canAddProducts: boolean;
  hasDynamicSize: boolean;
  basePrice: number;
  bundlePrice: number;
  discountedPrice: number | null;
  productsLength: number;
  getItemProperties: () => BundleCartProperties;
  removeProduct: (productId: GlobalVariant | GlobalProduct) => void;
  addProduct: (productId: GlobalVariant | GlobalProduct) => void;
  addAddon: (productId: GlobalVariant) => void;
  removeAddon: (productId: GlobalVariant) => void;
  selectedProducts: Record<string, number>;
  initialSelectedProducts: Record<string, number>;
  selectedAddons: Record<string, number>;
  updateBoxSize: (variantId: string) => void;
  getSelectedProductsWithPropsForCart: () => ProductWithProps[];
  variantId: string;
  dynamicVariantId: string;
  dynamicDiscountCode: string;
  sellingPlan: string | number;
  setSellingPlan: (sellingPlan: string | number) => void;
  setSelectedProducts: (newSelectedProducts: Record<string, number>) => void;
  setSelectedAddons: (newSelectedProducts: Record<string, number>) => void;
  size: number;
  sellingPlans: SellingPlan[];
  settings: BundleSetting | Record<string, never>;
}

export type BasePropertiesType = {
  _bundleLabel: string;
  _bundleProductId: string;
  _bundleName: string;
  _bundleBoxSize: number;
  _variantId: string;
  _bundleBoxPrice: number;
  _bundleBoxMinSize?: number;
  _bundleBoxMaxSize?: number;
};

type UseCreateBundleProps = {
  bundle: Bundle;
  productsInCollection: GlobalVariant[] | GlobalProduct[];
  productsInAddonsCollection: GlobalVariant[] | GlobalProduct[];
  initialSelectedProducts?: Record<string, number>;
  sellingPlans?: SellingPlan[];
  subscription?: Subscription;
  shopConfiguration?: ShopConfiguration;
  selectedDynamicVariantId?: string;
  productBundle?: LiquidProduct;
  hasDuplicatedLines?: boolean;
  product?: {
    selling_plan_groups: SellingPlanGroup[];
    requires_selling_plan?: boolean;
    error?: string;
    variants?: any[];
  };
};

type LinePrice = {
  CurrentPriceAmount: number;
  PricingPolicyBasePriceAmount: number;
};

export const getPrice = (productBundle, settings, variantId) => {
  if (productBundle?.variants && variantId) {
    const selectedVariant = productBundle?.variants.find(
      (variant) => variant.id === +getIdFromGid(variantId),
    );
    if (selectedVariant) {
      return +selectedVariant.price / 100;
    }
  }
  return +get(settings, ['Price'], 0);
};

export const MAX_BUNDLE_SIZE_DEFAULT = 1000;

export const useSizeSelector = ({
  bundle,
  settings,
  setVariantId,
}: {
  bundle: Bundle;
  settings: BundleSetting;
  setVariantId: (variantId: string) => void;
}): {
  size: number;
  minSize: number;
  maxSize: number;
  stepSize: number;
  hasDynamicSize: boolean;
  updateBoxSize: (variantId: string) => void;
} => {
  const isDynamic = bundle?.Type === 'dynamicBaB';

  const size = +get(settings, ['Size'], 0);
  const minSize = isDynamic ? +get(settings, ['MinSize'], 0) : size;
  const maxSize = isDynamic
    ? +get(settings, ['MaxSize'], minSize > 0 ? MAX_BUNDLE_SIZE_DEFAULT : 0)
    : size;

  const stepSize = +get(settings, ['StepSize'], 1);
  const hasDynamicSize = isDynamic && minSize !== maxSize;

  const updateBoxSize = (variantId: string) => {
    setVariantId(variantId);
  };

  return { size, minSize, maxSize, stepSize, hasDynamicSize, updateBoxSize };
};

export const useBundleProperties = ({
  settings,
  bundle,
  size,
  variantId,
  price,
  hasDynamicSize,
  minSize,
  maxSize,
  selectedProducts,
  productsInCollection,
}): {
  baseProperties: BasePropertiesType;
  getItemProperties: () => BundleCartProperties;
} => {
  let baseProperties: BasePropertiesType = {
    _bundleLabel: settings.Label,
    _bundleProductId: bundle.ProductId,
    _bundleName: bundle.Name,
    _bundleBoxSize: size,
    _variantId: variantId,
    _bundleBoxPrice: price,
  };

  if (hasDynamicSize) {
    baseProperties = {
      ...baseProperties,
      _bundleBoxMinSize: minSize,
      _bundleBoxMaxSize: maxSize,
    };
  }

  const getItemProperties = getItemPropertiesFromState(
    baseProperties,
    selectedProducts,
    productsInCollection,
  );

  return { baseProperties, getItemProperties };
};

export const useBundlePrice = ({
  settings,
  selectedProducts,
  productsInCollection,
  product,
  sellingPlan,
  isDynamic,
  productBundle,
  variantId,
  subscription,
  selectedAddons,
  productsInAddonsCollection,
}: {
  settings: BundleSetting;
  selectedProducts: Record<string, number>;
  productsInCollection: any[];
  product: {
    selling_plan_groups: SellingPlanGroup[];
    requires_selling_plan?: boolean;
    variant?: GlobalVariant;
    error?: string;
  };
  sellingPlan: string | number;
  isDynamic: boolean;
  productBundle: LiquidProduct;
  variantId: string;
  subscription: Subscription;
  selectedAddons: Record<string, number>;
  productsInAddonsCollection: GlobalVariant[] | GlobalProduct[];
}): {
  bundlePrice: number;
  discountedPrice: number;
  basePrice: number;
  price: number;
} => {
  const babProduct = product || productBundle;
  const price = getPrice(babProduct, settings, variantId);

  const subscriptionLinePrices = useMemo(
    () =>
      getDynamicSubscriptionLinePrices({ lines: subscription?.Lines || [] }),
    [subscription],
  );

  const discount =
    babProduct && !babProduct.error
      ? getDiscountFromSellingPlan(babProduct, price, sellingPlan)
      : getDiscountFromUrl(price);

  const basePrice = !isDynamic
    ? isNumber(discount)
      ? price
      : discount?.value / 100 || price
    : calculateDiscountedBabPrice({
        selectedProducts,
        productsInCollection,
        subscriptionPrices: subscriptionLinePrices,
      });

  const [bundlePrice, setBundlePrice] = React.useState<number>(basePrice);

  const getDiscountedPrice = (price) => {
    if (isNumber(discount)) {
      return price - discount;
    } else if (typeof discount === 'object' && discount?.value) {
      return discount.value / 100;
    }

    return price;
  };

  const discountedPrice = isDynamic
    ? calculateDiscountedBabPrice({
        selectedProducts,
        productsInCollection,
        subscriptionPrices: subscriptionLinePrices,
        sellingPlan: sellingPlan as string,
        settings: settings as BundleSetting,
      })
    : getDiscountedPrice(basePrice);

  useEffect(() => {
    const bundlePrice = getAddonPrices(
      selectedAddons,
      productsInAddonsCollection,
    );

    setBundlePrice(bundlePrice);
  }, [basePrice, productsInAddonsCollection, selectedAddons]);

  return { bundlePrice, discountedPrice, basePrice, price };
};

const useSellingPlan = ({
  bundle,
}: {
  bundle: Bundle;
}): {
  sellingPlan: string | number;
  updateSellingPlan: (sellingPlan: string | number) => void;
} => {
  const [sellingPlan, setSellingPlan] = React.useState<string | number>(
    getBundleSellingPlan(bundle.SellingPlan),
  );

  const updateSellingPlan = (sellingPlan: string | number) => {
    setSellingPlan(sellingPlan);
    const params = new URLSearchParams(location.search);
    params.set(
      'selling_plan',
      sellingPlan === '' ? ONE_TIME_SELLING_PLAN : sellingPlan.toString(),
    );

    window.history.replaceState({}, '', `${location.pathname}?${params}`);
  };

  return { sellingPlan, updateSellingPlan };
};

const useSelectedProducts = ({
  hasDuplicatedLines = false,
  initialSelectedProducts,
  productsInCollection,
  size,
  minSize,
  maxSize,
  isDynamic,
  hasDynamicSize,
  subscription,
  shopConfiguration,
  variantId,
  stepSize,
}) => {
  const { ignoreBaBOutOfStockAllowance } = useGlobalContext();
  const [selectedProducts, setSelectedProducts] = React.useState<
    Record<string, number>
  >(removeUnexistingProducts(initialSelectedProducts, productsInCollection));

  const productsLength = useMemo(
    () => getProductsLength(selectedProducts),
    [selectedProducts],
  );

  const isFullSize = productsLength === size;
  const canAddProducts =
    isDynamic && hasDynamicSize
      ? productsLength < maxSize
      : productsLength < size;
  const isSaveable =
    isDynamic && hasDynamicSize
      ? productsLength >= minSize && productsLength <= maxSize
      : isFullSize;

  const baseSelectedProducts = React.useMemo(() => {
    const currentLine = subscription?.Lines?.find(
      ({ VariantId }) =>
        VariantId === getGidFromIdForResource(variantId, 'ProductVariant'),
    );
    const newSelectedProducts = removeOutOfStockVariants(
      removeUnexistingProducts(initialSelectedProducts, productsInCollection),
      currentLine?.BaBOutOfStockVariants || [],
      shopConfiguration?.ShopOversell,
    );

    return newSelectedProducts;
  }, [
    initialSelectedProducts,
    productsInCollection,
    shopConfiguration?.ShopOversell,
    subscription?.Lines,
    variantId,
  ]);

  const removeProduct = (product: GlobalVariant | GlobalProduct) => {
    const newSelectedProducts = removeProductToSelected(
      product,
      selectedProducts,
    );

    setSelectedProducts(newSelectedProducts);
  };

  const addProduct = (product: GlobalVariant | GlobalProduct) => {
    const maxProductsToAdd =
      size - productsLength < 1 ? 1 : size - productsLength;
    const step = stepSize > maxProductsToAdd ? maxProductsToAdd : stepSize;
    const newSelectedProducts = addProductToSelected(
      product,
      selectedProducts,
      step,
    );
    setSelectedProducts(newSelectedProducts);
  };

  useEffect(() => {
    let filteredInitialProducts = removeUnexistingProducts(
      merge(initialSelectedProducts, selectedProducts),
      productsInCollection,
    );

    const outOfStockProducts = removeVariantsWithIgnoredOversell(
      productsInCollection,
      shopConfiguration,
      ignoreBaBOutOfStockAllowance,
    );
    filteredInitialProducts = omit(filteredInitialProducts, outOfStockProducts);

    setSelectedProducts(filteredInitialProducts);
  }, [initialSelectedProducts, productsInCollection]);

  useEffect(() => {
    if (productsLength > size && !minSize && !maxSize && !hasDuplicatedLines) {
      setSelectedProducts(removeProductsForCorrectSize(selectedProducts, size));
    }
  }, [selectedProducts, productsLength, size, minSize, maxSize]);

  return {
    selectedProducts,
    baseSelectedProducts,
    productsLength,
    isFullSize,
    isSaveable,
    canAddProducts,
    removeProduct,
    addProduct,
    setSelectedProducts,
  };
};

export const useSelectedAddons = () => {
  const [selectedAddons, setSelectedAddons] = React.useState<
    Record<string, number>
  >({});

  const addAddon = (product: GlobalVariant) => {
    const newSelectedProducts = addProductToSelected(product, selectedAddons);

    setSelectedAddons(newSelectedProducts);
  };

  const removeAddon = (product: GlobalVariant) => {
    const newSelectedProducts = removeProductToSelected(
      product,
      selectedAddons,
    );

    setSelectedAddons(newSelectedProducts);
  };

  return { selectedAddons, addAddon, removeAddon, setSelectedAddons };
};

export const useBundleSettings = ({
  variantId,
  settings,
  productsLength,
  bundle,
}: {
  variantId: string;
  settings: BundleSetting;
  productsLength: number;
  bundle: Bundle;
}) => {
  const isDynamic = bundle.Type === 'dynamicBaB';
  const [dynamicVariantId, setDynamicVariantId] = React.useState<string>(
    (variantId && getGidFromIdForResource(variantId, 'ProductVariant')) ||
      settings?.[0].VariantId,
  );

  const sortedBySizeSettings = useMemo(() => {
    return [settings].sort((a, b) => parseFloat(b.Size) - parseFloat(a.Size));
  }, [settings]);

  const { updateBundleSetting, bundleSetting } = useBundleSetting();

  useDeepCompareEffect(() => {
    const shopDomain = bundle.PK?.replace(/SHOP#/, '');
    const getBundleSetting = async () => {
      const setting = await getBundleDiscounts({
        bundleId: getIdFromGid(bundle.ProductId),
        variantId: getIdFromGid(variantId),
        shopDomain,
      });

      return setting;
    };

    getBundleSetting().then((setting) => {
      updateBundleSetting(setting);
    });
  }, [bundle.PK, variantId, bundle.ProductId, updateBundleSetting]);

  const dynamicDiscountCode = isDynamic
    ? bundleSetting?.DynamicDiscountCode
    : '';

  useEffect(() => {
    const setting = sortedBySizeSettings.find(
      (setting) => productsLength >= parseFloat(setting.Size),
    );

    if (setting) {
      setDynamicVariantId(setting.VariantId);
    }
  }, [productsLength, sortedBySizeSettings]);

  useEffect(() => {
    if (settings) {
      updateBundleSetting(settings);
    }
  }, [settings, updateBundleSetting]);

  return { dynamicVariantId, dynamicDiscountCode };
};

export const useCreateBundle = ({
  bundle,
  productsInCollection,
  productsInAddonsCollection,
  initialSelectedProducts = {},
  subscription,
  shopConfiguration,
  selectedDynamicVariantId,
  productBundle,
  product,
  hasDuplicatedLines,
}: UseCreateBundleProps): UseCreateBundle => {
  const isDynamic = bundle?.Type === 'dynamicBaB';

  const [variantId, setVariantId] = React.useState<string>(
    (isDynamic && selectedDynamicVariantId
      ? selectedDynamicVariantId
      : bundle.VariantId &&
        getGidFromIdForResource(bundle.VariantId, 'ProductVariant')) ||
      bundle.Settings?.[0].VariantId,
  );

  const settings = findBundleSettings({
    settings: bundle.Settings,
    variantId,
  }) as BundleSetting;

  const { size, minSize, maxSize, stepSize, hasDynamicSize, updateBoxSize } =
    useSizeSelector({
      bundle,
      settings,
      setVariantId,
    });

  const {
    selectedProducts,
    baseSelectedProducts,
    productsLength,
    isFullSize,
    isSaveable,
    canAddProducts,
    removeProduct,
    addProduct,
    setSelectedProducts,
  } = useSelectedProducts({
    hasDuplicatedLines,
    initialSelectedProducts,
    productsInCollection,
    size,
    minSize,
    maxSize,
    isDynamic,
    hasDynamicSize,
    subscription,
    shopConfiguration,
    variantId,
    stepSize,
  });

  const babProduct = product || productBundle;

  const variant =
    babProduct?.variants?.find(({ id }) => {
      return variantId === getGidFromIdForResource(id, 'ProductVariant');
    }) ||
    (babProduct?.variants && babProduct?.variants[0]) ||
    {};

  const spa = variant?.selling_plan_allocations?.map?.(
    ({ selling_plan_id }) => selling_plan_id,
  );

  const [sellingPlans, setSellingPlans] = React.useState(
    babProduct?.selling_plan_groups
      ?.flatMap((group) => group.selling_plans)
      ?.filter(({ id }) => spa?.includes(id)) || [],
  );

  useEffect(() => {
    if (!variantId) {
      return;
    }
    const variant =
      babProduct?.variants?.find(({ id }) => {
        return variantId === getGidFromIdForResource(id, 'ProductVariant');
      }) ||
      (babProduct?.variants && babProduct?.variants[0]) ||
      {};

    const spa = variant?.selling_plan_allocations?.map?.(
      ({ selling_plan_id }) => selling_plan_id,
    );

    const newSellingPlans =
      babProduct?.selling_plan_groups
        ?.flatMap((group) => group.selling_plans)
        ?.filter(({ id }) => spa?.includes(id)) || [];

    setSellingPlans(newSellingPlans);
  }, [variantId]);

  const { selectedAddons, addAddon, removeAddon, setSelectedAddons } =
    useSelectedAddons();

  const { sellingPlan, updateSellingPlan } = useSellingPlan({
    bundle,
  });

  const { bundlePrice, discountedPrice, basePrice, price } = useBundlePrice({
    settings,
    selectedProducts,
    productsInCollection,
    product,
    sellingPlan,
    isDynamic,
    productBundle,
    variantId,
    subscription,
    selectedAddons,
    productsInAddonsCollection,
  });

  const { baseProperties, getItemProperties } = useBundleProperties({
    bundle,
    settings,
    size,
    variantId,
    price,
    hasDynamicSize,
    minSize,
    maxSize,
    selectedProducts,
    productsInCollection,
  });

  const { dynamicVariantId, dynamicDiscountCode } = useBundleSettings({
    variantId,
    settings,
    productsLength,
    bundle,
  });

  const getSelectedProductsWithPropsForCart = () => {
    const babUuid = uuidv4();
    const babHandle = bundle.Handle;

    const selectedProductsWithProps = (productsInCollection as any[]).reduce(
      (acc, product: GlobalVariant | GlobalProduct) => {
        let variant;
        if (isGlobalProduct(product)) {
          variant = product.variants.find(({ id }) => {
            return !!Object.keys(selectedProducts).find(
              (key) => key === `${id}`,
            );
          });
        } else variant = product;

        if (!variant) {
          return acc;
        }

        const selectedProductCount = selectedProducts[variant.id];
        const { _bundleBoxPrice, ...baseProps } = baseProperties;
        if (selectedProductCount) {
          const productWithProps: ProductWithProps = {
            id: variant.id.toString(),
            quantity: selectedProductCount,
            properties: {
              _bundleUid: babUuid,
              _bundleHandle: babHandle,
              _bundleType: 'dynamicBaB',
              ...baseProps,
              ...(!isDynamic ? { _bundleBoxPrice } : {}),
            },
          };

          if (sellingPlan) {
            productWithProps.selling_plan = sellingPlan.toString();
          }

          return [...acc, productWithProps];
        }
        return acc;
      },
      [] as ProductWithProps[],
    );

    return selectedProductsWithProps;
  };

  return {
    isFullSize,
    isSaveable,
    canAddProducts,
    hasDynamicSize,
    getItemProperties,
    getSelectedProductsWithPropsForCart,
    basePrice,
    bundlePrice,
    discountedPrice,
    productsLength,
    removeProduct,
    addProduct,
    selectedProducts,
    addAddon,
    removeAddon,
    selectedAddons,
    updateBoxSize,
    variantId,
    dynamicVariantId,
    dynamicDiscountCode,
    sellingPlan,
    setSellingPlan: updateSellingPlan,
    setSelectedProducts,
    setSelectedAddons,
    size,
    settings,
    sellingPlans,
    initialSelectedProducts: baseSelectedProducts,
  };
};

// Utilities

export const addProductToSelected = (
  product: GlobalVariant | GlobalProduct,
  selectedProducts: Record<string, number>,
  stepSize = 1,
): Record<string, number> => {
  const newSelectedProducts = { ...selectedProducts };
  if (newSelectedProducts[product.id]) {
    newSelectedProducts[product.id] =
      newSelectedProducts[product.id] + stepSize;
  } else {
    newSelectedProducts[product.id] = stepSize;
  }
  return newSelectedProducts;
};

export const removeProductToSelected = (
  product: GlobalVariant | GlobalProduct,
  selectedProducts: Record<string, number>,
): Record<string, number> => {
  const newSelectedProducts = { ...selectedProducts };
  if (newSelectedProducts[product.id] && newSelectedProducts[product.id] > 0) {
    newSelectedProducts[product.id] = newSelectedProducts[product.id] - 1;
  } else {
    newSelectedProducts[product.id] = 0;
  }
  return newSelectedProducts;
};

const getAddonPrices = (
  selectedAddons: Record<string, number>,
  productsInAddonsCollection: GlobalVariant[] | GlobalProduct[],
): number => {
  const addonPrice = Object.entries(selectedAddons).reduce(
    (acc, [key, value]) => {
      let addon;
      if (isGlobalProductList(productsInAddonsCollection)) {
        addon = productsInAddonsCollection
          .find(({ variants }) => {
            return variants.find(({ id }) => `${id}` === key);
          })
          ?.variants?.find(({ id }) => `${id}` === key);
      } else {
        addon = productsInAddonsCollection.find(
          (addon) => `${addon.id}` === key,
        );
      }

      if (addon) {
        return acc + getAddonPrice(addon) * value;
      }

      return acc;
    },
    0,
  );

  return addonPrice;
};

export const findBundleSettings = ({
  settings = [],
  variantId = '',
}: {
  settings: BundleSetting[];
  variantId: string;
}): BundleSetting | Record<string, never> => {
  const bundleSettings =
    settings.find(
      (setting) =>
        getIdFromGid(setting.VariantId) === variantId ||
        setting.VariantId === variantId,
    ) || settings[0];

  if (!bundleSettings) {
    return {};
  }

  return bundleSettings;
};

export const calculateDiscountedBabPrice = ({
  selectedProducts,
  productsInCollection,
  subscriptionPrices,
  sellingPlan,
  settings,
}: {
  selectedProducts: Record<string, number>;
  productsInCollection: GlobalVariant[] | GlobalProduct[];
  subscriptionPrices?: Record<string, LinePrice>;
  sellingPlan?: string;
  settings?: BundleSetting;
}): number => {
  const productPrice = Object.entries(selectedProducts).reduce(
    (acc, [key, value]) => {
      const product = getProductOrVariant(productsInCollection, key);
      if (product) {
        const prices =
          subscriptionPrices?.[
            getGidFromIdForResource(product.id.toString(), 'ProductVariant')
          ];

        const currentPrice =
          prices?.CurrentPriceAmount ||
          Number(
            getPriceFromSellingPlanAllocations(
              product,
              sellingPlan || '',
            )?.toFixed(2),
          ) ||
          Number((getProductCurrentPrice(product) as number).toFixed(2));

        const price = applyDiscountsToLine({
          price: currentPrice,
          quantity: 1,
          discounts: [settings].filter(Boolean),
        });
        return acc + Number((price * value).toFixed(2));
      }

      return acc;
    },
    0,
  );

  return Number(productPrice.toFixed(2));
};

export const getBundleSellingPlan = (sellingPlan) => {
  if (sellingPlan === ONE_TIME_SELLING_PLAN || !sellingPlan) {
    return '';
  }

  const params = new URLSearchParams(location.search);
  const sp = params.get('selling_plan');

  if (sp) {
    return sp;
  }

  return sellingPlan;
};

const getProductOrVariant = (productsInCollection, key) => {
  let product;
  if (isGlobalProductList(productsInCollection)) {
    product = productsInCollection
      .find(({ variants }) => {
        return variants.find(({ id }) => `${id}` === key);
      })
      ?.variants?.find(({ id }) => `${id}` === key);
  } else {
    product = productsInCollection.find((product) => `${product.id}` === key);
  }

  return product;
};

/*
 * This check implements the ignoreOutOfStockAllowance feature
 * which, if enabled, marks a product as sold out even if
 * the shop configuration allows for overselling.
 */
const removeVariantsWithIgnoredOversell = (
  productsInCollection: GlobalVariant[] | GlobalProduct[],
  shopConfiguration: ShopConfiguration,
  ignoreBaBOutOfStockAllowance: boolean,
) =>
  (isGlobalProductList(productsInCollection)
    ? flatMap(productsInCollection, ({ variants }) => variants)
    : productsInCollection
  )
    .filter(
      (item) =>
        !isProductAvailable(
          item,
          shopConfiguration,
          ignoreBaBOutOfStockAllowance,
        ),
    )
    .map(({ id }) => id);
